import React, { useMemo } from 'react'

import SystemMonitorChart from './SystemMonitorChart'

const clamp = (value, min = 0, max = 100) =>
  Math.min(max, Math.max(min, Number(value) || 0))

const formatBytes = (value) => {
  const bytes = Number(value || 0)
  if (!(bytes > 0)) return '0 Б'
  const units = ['Б', 'КБ', 'МБ', 'ГБ', 'ТБ']
  const index = Math.min(
    units.length - 1,
    Math.floor(Math.log(bytes) / Math.log(1024)),
  )
  const number = bytes / 1024 ** index
  return `${number >= 100 ? number.toFixed(0) : number.toFixed(1)} ${units[index]}`
}

const formatRate = (value) => `${formatBytes(value)}/с`

const formatMs = (value) => {
  const ms = Number(value || 0)
  if (ms >= 1000) return `${(ms / 1000).toFixed(ms >= 10000 ? 1 : 2)} с`
  return `${Math.round(ms * 10) / 10} мс`
}

const metricTone = (pressure) => {
  if (pressure >= 85) return 'bad'
  if (pressure >= 60) return 'warn'
  return 'good'
}

const MetricGauge = ({ metric, detail }) => {
  const pressure = clamp(metric?.pressurePercent)
  const tone = metricTone(pressure)

  return (
    <article className={`systemMonitor__loadMetric is-${tone}`}>
      <div className="systemMonitor__loadMetricTop">
        <div>
          <span>{metric?.label || 'Показатель'}</span>
          <strong>
            {metric?.value ?? 0}
            {metric?.unit || ''}
          </strong>
        </div>
        <b>{Math.round(pressure)}%</b>
      </div>
      <div className="systemMonitor__loadBar" aria-hidden="true">
        <i style={{ width: `${pressure}%` }} />
      </div>
      <div className="systemMonitor__loadMetricMeta">
        <span>
          {detail ||
            `Безопасная зона: до ${metric?.safeValue ?? '—'}${
              metric?.unit || ''
            }`}
        </span>
        <span>
          критично: {metric?.criticalValue ?? '—'}
          {metric?.unit || ''}
        </span>
      </div>
    </article>
  )
}

const CapacityNumber = ({ label, value, sub, accent = false }) => (
  <div
    className={`systemMonitor__capacityNumber${accent ? ' is-accent' : ''}`}
  >
    <span>{label}</span>
    <strong>{value}</strong>
    {sub ? <small>{sub}</small> : null}
  </div>
)

const ApiWindowCard = ({ item, selected = false }) => {
  const tone = item?.status?.tone || 'neutral'
  const reliable = Boolean(item?.status?.reliable)

  return (
    <div
      className={`systemMonitor__apiWindow is-${tone}${
        selected ? ' is-selected' : ''
      }`}
    >
      <div className="systemMonitor__apiWindowHead">
        <span>{item?.label || 'Период'}</span>
        <b>{item?.status?.label || 'НЕТ ДАННЫХ'}</b>
      </div>
      <strong>{reliable ? formatMs(item?.p95Ms) : '—'}</strong>
      <small>
        {item?.requests || 0} запросов · среднее {formatMs(item?.avgMs)}
      </small>
      <div className="systemMonitor__apiWindowBar">
        <i
          style={{
            width: `${clamp(item?.status?.pressurePercent || 0)}%`,
          }}
        />
      </div>
      <p>{item?.status?.note || 'Система собирает статистику.'}</p>
    </div>
  )
}

const SystemMonitorLoad = ({
  data,
  history = [],
  api = {},
  socket = {},
  sourcePresence = {},
  source = 'all',
}) => {
  const server = data?.server || {}
  const mongo = data?.mongo || {}
  const capacity = data?.capacity || {}
  const estimate = capacity.estimate || {}
  const observation = capacity.observation || {}
  const current = capacity.current || {}
  const metrics = capacity.metrics || []
  const status = capacity.status || {
    label: 'СОБИРАЕМ ДАННЫЕ',
    tone: 'neutral',
  }
  const pressure = clamp(
    capacity.serverLoadPercent ?? capacity.pressurePercent,
  )
  const bottleneck = capacity.bottleneck || {}
  const apiQuality = capacity.apiQuality || {}
  const apiWindows = apiQuality.windows || {}
  const apiCurrent = apiQuality.current || {}

  const metricDetails = useMemo(
    () => ({
      cpu: `Node.js: ${server.processCpuPercent ?? 0}% · ${
        server.cpuCores || 0
      } ядер VPS`,
      ram: `${formatBytes(server.memory?.usedBytes)} из ${formatBytes(
        server.memory?.totalBytes,
      )}`,
      eventLoop: `Среднее: ${server.eventLoop?.meanMs || 0} мс · максимум: ${
        server.eventLoop?.maxMs || 0
      } мс`,
      mongo: `Состояние: ${
        mongo.connected ? 'подключена' : 'нет соединения'
      }`,
      errors: `5xx: ${api.status?.server5xx || 0} · 4xx: ${
        api.status?.client4xx || 0
      }`,
    }),
    [api, mongo, server],
  )

  const topUsers = api.topUsers || []
  const sourceLabel =
    source === 'front'
      ? 'САЙТ'
      : source === 'admin'
        ? 'АДМИНКА'
        : 'ВЕСЬ ПРОЕКТ'

  const bottleneckPressure = clamp(bottleneck.pressurePercent || 0)
  const hasBottleneck = bottleneckPressure >= 40
  const apiIsSlow =
    apiCurrent?.status?.reliable &&
    ['elevated', 'slow', 'critical'].includes(apiCurrent?.status?.key)

  return (
    <div className="systemMonitor__loadDashboard">
      {capacity.testMode?.active ? (
        <div className="systemMonitor__loadTestBanner">
          <div>
            <b>ИДЁТ КОНТРОЛИРУЕМЫЙ НАГРУЗОЧНЫЙ ТЕСТ</b>
            <span>{capacity.testMode.note}</span>
          </div>
          <strong>
            {capacity.testMode.requestsInWindow || 0} тестовых запросов
          </strong>
        </div>
      ) : null}

      <section className={`systemMonitor__loadHero is-${status.tone || 'neutral'}`}>
        <div className="systemMonitor__loadHeroMain">
          <div className="systemMonitor__eyebrow">ЗАПАС МОЩНОСТИ СЕРВЕРА</div>
          <h2>{status.label}</h2>
          <p>{capacity.summaryNote || 'Система собирает текущие показатели сервера.'}</p>
          <div className="systemMonitor__loadHeroBadges">
            <span>
              Сейчас:{' '}
              {current.peopleOnline ??
                (sourcePresence.usersOnline || 0) +
                  (sourcePresence.guestsOnline || 0)}{' '}
              чел.
            </span>
            <span>
              Сокетов: {current.socketsOnline ?? sourcePresence.socketsOnline ?? 0}
            </span>
            <span>
              API: {current.apiRequestsPerMinute ?? api.requestsPerMinute ?? 0}/мин
            </span>
            <span>Источник: {sourceLabel}</span>
          </div>
        </div>

        <div
          className="systemMonitor__loadDial"
          style={{ '--system-load-angle': `${pressure * 3.6}deg` }}
          aria-label={`Текущая загрузка сервера ${Math.round(pressure)} процентов`}
        >
          <div>
            <strong>{Math.round(pressure)}%</strong>
            <span>загрузка сервера</span>
          </div>
        </div>
      </section>

      <section className="systemMonitor__capacityPanel">
        <div className="systemMonitor__capacityHead">
          <div>
            <div className="systemMonitor__sectionTitle">
              Сколько пользователей выдержит сервер
            </div>
            <div className="systemMonitor__sectionHint">
              Это оценка, а не жёсткий лимит. Чем больше реальной истории и
              контролируемых тестов, тем точнее прогноз.
            </div>
          </div>
          <div
            className={`systemMonitor__confidence is-${
              estimate.confidence?.key || 'low'
            }`}
          >
            <span>Уверенность</span>
            <b>{estimate.confidence?.label || 'Низкая'}</b>
            <i>
              <em
                style={{ width: `${clamp(estimate.confidence?.percent)}%` }}
              />
            </i>
          </div>
        </div>

        <div className="systemMonitor__capacityNumbers">
          <CapacityNumber
            label="Сейчас в сети"
            value={current.peopleOnline ?? 0}
            sub={`${current.usersOnline || 0} пользователей · ${
              current.guestsOnline || 0
            } гостей`}
          />
          <CapacityNumber
            label="Проверено наблюдением"
            value={observation.peakPeople || 0}
            sub={`пик за ${observation.minutes || 0} мин наблюдения`}
          />
          <CapacityNumber
            label="Комфортная оценка"
            value={estimate.available ? `≈ ${estimate.comfortableUsers}` : 'собираем'}
            sub={
              estimate.available
                ? 'ориентир безопасной одновременной работы'
                : 'нужно больше активных посетителей'
            }
            accent
          />
          <CapacityNumber
            label="Верхняя оценка"
            value={estimate.available ? `≈ ${estimate.upperUsers}` : '—'}
            sub="не рекомендуется считать рабочим лимитом"
          />
          <CapacityNumber
            label="Оценочный запас"
            value={estimate.available ? `+${estimate.reserveUsers}` : '—'}
            sub="до комфортной оценки"
          />
        </div>

        <div className="systemMonitor__capacityNote">
          <b>
            {estimate.available
              ? 'Как читать прогноз:'
              : 'Почему пока нет точного числа:'}
          </b>
          <span>
            {estimate.reason || 'Система собирает фактическую статистику нагрузки.'}
          </span>
        </div>
      </section>

      <div className="systemMonitor__loadMetricsGrid">
        {metrics.map((metric) => (
          <MetricGauge
            key={metric.key}
            metric={metric}
            detail={metricDetails[metric.key]}
          />
        ))}
      </div>

      <section
        className={`systemMonitor__apiQuality is-${
          apiCurrent?.status?.tone || 'neutral'
        }`}
      >
        <div className="systemMonitor__apiQualityHead">
          <div>
            <div className="systemMonitor__eyebrow">СКОРОСТЬ API</div>
            <h3>{apiCurrent?.status?.label || 'СОБИРАЕМ ДАННЫЕ'}</h3>
            <p>
              API P95 показывает время, быстрее которого завершились 95% запросов.
              Этот показатель оценивает скорость API отдельно и больше не считается
              процентом загрузки сервера.
            </p>
          </div>
          <div className="systemMonitor__apiQualityValue">
            <span>Текущий P95</span>
            <strong>
              {apiCurrent?.status?.reliable ? formatMs(apiCurrent.p95Ms) : '—'}
            </strong>
            <small>
              {apiCurrent?.status?.reliable
                ? `${apiCurrent.label} · ${apiCurrent.requests || 0} запросов`
                : apiCurrent?.status?.note || 'Недостаточно свежих запросов'}
            </small>
          </div>
        </div>

        {pressure < 50 && apiIsSlow ? (
          <div className="systemMonitor__apiQualityNotice">
            <b>Сервер не перегружен.</b>
            <span>
              Красный или жёлтый статус здесь означает медленные API-ответы, а не
              нехватку CPU или памяти VPS.
            </span>
          </div>
        ) : null}

        <div className="systemMonitor__apiWindows">
          <ApiWindowCard item={apiWindows.minute} selected={apiCurrent.label === apiWindows.minute?.label} />
          <ApiWindowCard item={apiWindows.fiveMinutes} selected={apiCurrent.label === apiWindows.fiveMinutes?.label} />
          <ApiWindowCard item={apiWindows.hour} />
        </div>

        <div className="systemMonitor__apiQualityFoot">
          <span>
            Активных API-запросов: <b>{api.activeRequests || 0}</b>
          </span>
          <span>
            Средняя задержка за 5 минут: <b>{formatMs(api.avgMs)}</b>
          </span>
          <span>
            Запросов за 5 минут: <b>{api.requests || 0}</b>
          </span>
          <span>
            Ошибок 5xx: <b>{api.status?.server5xx || 0}</b>
          </span>
        </div>
      </section>

      <div className="systemMonitor__loadSummaryGrid">
        <section className="systemMonitor__section">
          <div className="systemMonitor__sectionTitle">Пики за период наблюдения</div>
          <div className="systemMonitor__loadPeakList">
            <div><span>Людей одновременно</span><b>{observation.peakPeople || 0}</b></div>
            <div><span>Сокетов одновременно</span><b>{observation.peakSockets || 0}</b></div>
            <div><span>CPU VPS</span><b>{observation.peakCpuPercent || 0}%</b></div>
            <div><span>Память</span><b>{observation.peakRamPercent || 0}%</b></div>
            <div><span>API P95</span><b>{formatMs(observation.peakApiP95Ms || 0)}</b></div>
            <div><span>API запросов/мин</span><b>{observation.peakApiRequestsPerMinute || 0}</b></div>
            <div><span>Цикл событий Node.js P95</span><b>{formatMs(observation.peakEventLoopP95Ms || 0)}</b></div>
          </div>
        </section>

        <section className="systemMonitor__section">
          <div className="systemMonitor__sectionTitle">Текущее системное узкое место</div>
          <div
            className={`systemMonitor__bottleneck is-${metricTone(
              bottleneckPressure,
            )}`}
          >
            <span>
              {hasBottleneck
                ? bottleneck.shortLabel || bottleneck.label
                : 'Выраженного узкого места нет'}
            </span>
            <strong>
              {bottleneck.value ?? 0}
              {bottleneck.unit || ''}
            </strong>
            <div className="systemMonitor__loadBar">
              <i style={{ width: `${bottleneckPressure}%` }} />
            </div>
            <small>
              {hasBottleneck
                ? `Это самый нагруженный системный показатель: около ${Math.round(
                    bottleneckPressure,
                  )}% его допустимого диапазона.`
                : `Самый высокий системный показатель использует только около ${Math.round(
                    bottleneckPressure,
                  )}% своего допустимого диапазона.`}
            </small>
          </div>
          <div className="systemMonitor__loadPeakList is-small">
            <div><span>Активных API-запросов</span><b>{api.activeRequests || 0}</b></div>
            <div><span>Socket.IO событий/мин</span><b>{socket.eventsPerMinute || 0}</b></div>
            <div><span>Входящий трафик</span><b>{formatRate(server.network?.rxBytesPerSecond)}</b></div>
            <div><span>Исходящий трафик</span><b>{formatRate(server.network?.txBytesPerSecond)}</b></div>
          </div>
        </section>
      </div>

      {apiQuality.slowEndpoints?.length ? (
        <section className="systemMonitor__section">
          <div className="systemMonitor__sectionHead">
            <div>
              <div className="systemMonitor__sectionTitle">Самые медленные API сейчас</div>
              <div className="systemMonitor__sectionHint">
                Сортировка по P95 в свежем окне. Это помогает отличить медленный
                endpoint от общей загрузки VPS.
              </div>
            </div>
          </div>
          <div className="systemMonitor__table">
            {apiQuality.slowEndpoints.map((row) => (
              <div
                className="systemMonitor__tableRow"
                key={`${row.method}:${row.endpoint}`}
              >
                <b>{row.method}</b>
                <span>{row.endpoint}</span>
                <span>{row.requests} запросов</span>
                <span>среднее {formatMs(row.avgMs)}</span>
                <strong>P95 {formatMs(row.p95Ms)}</strong>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <div className="systemMonitor__chartsGrid">
        <SystemMonitorChart
          title="Процессор и память"
          history={history}
          series={[
            { key: 'cpuPercent', label: 'CPU VPS, %' },
            { key: 'processCpuPercent', label: 'CPU Node.js, %' },
            { key: 'ramPercent', label: 'Память, %' },
          ]}
        />
        <SystemMonitorChart
          title="Пользователи и соединения"
          history={history}
          series={[
            { key: 'usersOnline', label: 'Пользователи' },
            { key: 'guestsOnline', label: 'Гости' },
            { key: 'socketsOnline', label: 'Сокеты' },
          ]}
        />
        <SystemMonitorChart
          title="API и задержка"
          history={history}
          series={[
            { key: 'apiRequestsPerMinute', label: 'Запросов/мин' },
            { key: 'apiP95Ms', label: 'P95, мс' },
            { key: 'eventLoopP95Ms', label: 'Цикл Node.js P95, мс' },
          ]}
        />
        <SystemMonitorChart
          title="Сетевой трафик"
          history={history}
          series={[
            { key: 'rxBytesPerSecond', label: 'Входящий, Б/с' },
            { key: 'txBytesPerSecond', label: 'Исходящий, Б/с' },
          ]}
        />
      </div>

      <section className="systemMonitor__section">
        <div className="systemMonitor__sectionHead">
          <div>
            <div className="systemMonitor__sectionTitle">Кто создаёт нагрузку</div>
            <div className="systemMonitor__sectionHint">
              Показываются API-запросы для выбранного источника: {sourceLabel}.
            </div>
          </div>
        </div>
        <div className="systemMonitor__table">
          {topUsers.map((row) => (
            <div className="systemMonitor__tableRow" key={row.userId}>
              <span>{row.name || row.email || row.userId}</span>
              <b>{row.requests} запросов</b>
              <span>среднее {formatMs(row.avgMs)}</span>
              <span>P95 {formatMs(row.p95Ms || 0)}</span>
              <span>{row.errors} ошибок</span>
            </div>
          ))}
          {!topUsers.length ? (
            <div className="systemMonitor__empty">Пока данных нет.</div>
          ) : null}
        </div>
      </section>
    </div>
  )
}

export default SystemMonitorLoad
